import type { QueryClient } from '@tanstack/react-query';
import { isTutorDashboardRefetchSuppressed } from '../../tutor/liveSessionActivity';
import { STUDENT_DASHBOARD_ROOT } from '../queryKeys';

export const DASHBOARD_PROGRESS_INVALIDATION_DEBOUNCE_MS = 400;

/** During Live/teardown finalization, mark dashboard summaries stale without network refetch. */
function dashboardInvalidationRefetchType(): 'active' | 'none' {
  return isTutorDashboardRefetchSuppressed() ? 'none' : 'active';
}

let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let listenerCount = 0;
let listenerCleanup: (() => void) | null = null;

function scheduleDashboardProgressInvalidation(queryClient: QueryClient) {
  if (debounceTimer != null) {
    clearTimeout(debounceTimer);
  }
  debounceTimer = setTimeout(() => {
    debounceTimer = null;
    const refetchType = dashboardInvalidationRefetchType();
    void queryClient.invalidateQueries({
      queryKey: [STUDENT_DASHBOARD_ROOT, 'progress-analytics'],
      refetchType,
    });
    void queryClient.invalidateQueries({
      queryKey: [STUDENT_DASHBOARD_ROOT, 'tutor-analytics-summary'],
      refetchType,
    });
  }, DASHBOARD_PROGRESS_INVALIDATION_DEBOUNCE_MS);
}

function onProgressUpdated(queryClient: QueryClient) {
  scheduleDashboardProgressInvalidation(queryClient);
}

/**
 * Coalesced listener for dashboard query invalidation after tutor progress updates.
 * Does not invalidate Patch F progress-batch or runtime-quota queries.
 */
export function acquireDashboardProgressInvalidationBridge(queryClient: QueryClient): () => void {
  listenerCount += 1;
  if (listenerCount === 1) {
    const handler = () => onProgressUpdated(queryClient);
    window.addEventListener('tkm-student-tutor-progress-updated', handler);
    listenerCleanup = () => {
      window.removeEventListener('tkm-student-tutor-progress-updated', handler);
      if (debounceTimer != null) {
        clearTimeout(debounceTimer);
        debounceTimer = null;
      }
    };
  }

  return () => {
    listenerCount = Math.max(0, listenerCount - 1);
    if (listenerCount === 0 && listenerCleanup) {
      listenerCleanup();
      listenerCleanup = null;
    }
  };
}

/** Test-only reset for bridge reference counting. */
export function __resetDashboardProgressInvalidationBridgeForTests(): void {
  listenerCount = 0;
  if (listenerCleanup) {
    listenerCleanup();
    listenerCleanup = null;
  }
  if (debounceTimer != null) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }
}
